<template>
  <ResumeBase
    :breadcrumbs="[
      {
        route: 'my-experience',
        label: t('resume.breadcrumbs.workExperiences'),
        type: 'parent',
      },
      {
        label: t('resume.breadcrumbs.workExperienceDetail'),
      },
    ]"
    mainContentId="resume-work-experience-detail-main-content"
    :formSaveRetryFunction="saveForm"
    :skipOnRouteBeforeLeave="skipOnRouteBeforeLeave"
    :formDeleteFunction="deleteWorkExperience"
    :triggerDeleteModal="triggerDeleteModal"
    :dismissDeleteModal="dismissDeleteModal"
  >
    <template v-slot:title>
      {{ t("resume.workExperienceForm.title") }}
    </template>
    <template v-slot:description>
      {{ t("resume.workExperienceForm.description") }}
    </template>
    <template v-slot:main-content>
      <form :aria-label="t('resume.workExperienceForm.title')">
        <TextInput
          data-test="role-input"
          :errorText="errorMessageMap.get('role')"
          :labelText="t('resume.workExperienceForm.roleInput.label')"
          :placeholderText="
            t('resume.workExperienceForm.roleInput.placeholder')
          "
          :value="role || ''"
          :isLoading="resumeStore.isResumeLoading"
          :required="true"
          :errorIdPrefix="errorIdPrefix"
          @onChange="
            (event) => {
              validateRole(event);
              setValue('role', event.detail.value);
            }
          "
          @onBlur="validateRole"
          @onUpdate="updateErrorDescription"
        />
        <TextInput
          data-test="business-input"
          :labelText="t('resume.workExperienceForm.businessInput.label')"
          :placeholderText="
            t('resume.workExperienceForm.businessInput.placeholder')
          "
          :value="business || ''"
          :isLoading="resumeStore.isResumeLoading"
          @onChange="(event) => setValue('business', event.detail.value)"
        />
        <TextInput
          data-test="start-date-input"
          class="start-date-input"
          :errorText="errorMessageMap.get('startDate')"
          :labelText="t('resume.workExperienceForm.startDateInput.label')"
          :placeholderText="
            t('resume.workExperienceForm.startDateInput.placeholder')
          "
          :value="startDate || ''"
          :isLoading="resumeStore.isResumeLoading"
          :errorIdPrefix="errorIdPrefix"
          @onChange="(event) => setValue('startDate', event.detail.value)"
          @onBlur="validateStartDate"
          @onUpdate="updateErrorDescription"
        />
        <CheckboxInput
          data-test="is-current-role-input"
          :checked="isCurrentRole || false"
          :labelText="t('resume.workExperienceForm.isCurrentRoleInput.label')"
          :isLoading="resumeStore.isResumeLoading"
          @onChange="(checked) => isCurrentRoleChange(checked)"
        />
        <TextInput
          data-test="end-date-input"
          class="end-date-input"
          :errorText="errorMessageMap.get('endDate')"
          :labelText="t('resume.workExperienceForm.endDateInput.label')"
          :placeholderText="
            t('resume.workExperienceForm.endDateInput.placeholder')
          "
          :value="endDate || ''"
          :isLoading="resumeStore.isResumeLoading"
          :disabled="isCurrentRole"
          :errorIdPrefix="errorIdPrefix"
          @onChange="(event) => setValue('endDate', event.detail.value)"
          @onBlur="validateEndDate"
          @onUpdate="updateErrorDescription"
        />
        <TextAreaInput
          data-test="business-description-input"
          class="business-description-input"
          :labelText="
            t('resume.workExperienceForm.businessDescriptionInput.label')
          "
          :errorText="errorMessageMap.get('businessDescription')"
          :maxLength="600"
          :placeholderText="
            t('resume.workExperienceForm.businessDescriptionInput.placeholder')
          "
          :autogrow="true"
          :value="businessDescription || ''"
          :isLoading="resumeStore.isResumeLoading"
          @onChange="
            (event) => {
              setValue('businessDescription', event.detail.value);
              validateBusinessDescription(event);
            }
          "
        />
        <TextAreaInput
          data-test="impact-input"
          :labelText="t('resume.workExperienceForm.impactInput.label')"
          :errorText="errorMessageMap.get('impact')"
          :maxLength="600"
          :placeholderText="
            t('resume.workExperienceForm.impactInput.placeholder')
          "
          :autogrow="true"
          :value="impact || ''"
          :isLoading="resumeStore.isResumeLoading"
          @onFocus="initialBulletPoint"
          @onBlur="removeBulletPoint"
          @paste="handlePaste"
          @keydown.enter="injectBulletPointOnEnter"
          @onChange="
            (event) => {
              setValue('impact', event.detail.value);
              validateImpact(event);
            }
          "
        />
        <div class="buttons-container">
          <AppButton
            size="default"
            type="primary"
            :label="t('resume.workExperienceForm.saveBtnLabel')"
            :ariaLabel="t('resume.workExperienceForm.saveBtnLabel')"
            @click="saveForm"
            data-test="save-form-btn"
            class="save-form-btn"
            width="100%"
            :isLoading="isFormSaving"
            :disabled="!isFormSavingEnabled"
            :ariaRoleDescription="saveButtonDescription"
          />
          <AppButton
            size="default"
            type="secondary"
            :label="t('resume.workExperienceForm.deleteBtnLabel')"
            :ariaLabel="t('resume.workExperienceForm.deleteBtnLabel')"
            data-test="delete-form-btn"
            class="delete-form-btn"
            @click="triggerDelete"
            :isLoading="isWorkExperienceDeleting"
            :disabled="!workExperienceId"
            width="100%"
          />
          <AppButton
            size="default"
            type="textOnly"
            :label="t('resume.workExperienceForm.closeBtnLabel')"
            :ariaLabel="t('resume.workExperienceForm.closeBtnLabel')"
            data-test="close-form-btn"
            class="close-form-btn"
            id="closeBtn"
            @click="returnToPage"
            width="100%"
          />
        </div>
      </form>
    </template>
    <template v-slot:resume-helper> </template>
  </ResumeBase>
</template>
<script setup lang="ts">
import ResumeBase from "@/Layouts/ResumeBase/ResumeBase.vue";
import TextInput from "@/components/Common/Inputs/TextInput/TextInput.vue";
import TextAreaInput from "@/components/Common/Inputs/TextAreaInput/TextAreaInput.vue";
import CheckboxInput from "@/components/Common/Inputs/CheckboxInput/CheckboxInput.vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import { useResumeStore, useErrorStore } from "@/store";
import { WorkExperience } from "@/store/models/resume.models";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, onBeforeRouteLeave, useRoute } from "vue-router";
import { onIonViewDidEnter } from "@ionic/vue";
import { handleBeforeUnload, focusOnMainContent } from "@/helpers";
const { t } = useI18n<{}>({
  useScope: "global",
});
import {
  IonInputCustomEvent,
  IonInput,
  IonTextareaCustomEvent,
} from "@ionic/core/components";
import {
  collateErrorsInDescription,
  parseDate,
  validateDateFormat,
} from "@/helpers";

const resumeStore = useResumeStore();
const errorStore = useErrorStore();
const router = useRouter();
const route = useRoute();

const skipOnRouteBeforeLeave = ref(false);
const triggerDeleteModal = ref(false);

onBeforeRouteLeave(() => {
  resumeStore.setWorkExperienceFormInit(false);
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

onMounted(async () => {
  if (!resumeStore.resumeId) {
    await resumeStore.requestResume();
    if (!resumeStore.resumeId) {
      await resumeStore.createResume();
    }
  }
  const workExperienceId = router.currentRoute.value.params.id as string;
  workExperienceId !== "new"
    ? resumeStore.initWorkExperienceForm(workExperienceId)
    : resumeStore.initWorkExperienceForm();
  initValidationMap();
});

onIonViewDidEnter(() => {
  focusOnMainContent(route?.meta?.mainContentId);
  skipOnRouteBeforeLeave.value = false;
  window.addEventListener("beforeunload", handleBeforeUnload);
});

const workExperienceId = computed(() => {
  return resumeStore.workExperienceFormData?.id;
});

const role = computed(() => {
  return resumeStore.workExperienceFormData?.role;
});

const business = computed(() => {
  return resumeStore.workExperienceFormData?.business;
});

const startDate = computed(() => {
  return resumeStore.workExperienceFormData?.startDate;
});

const endDate = computed(() => {
  return resumeStore.workExperienceFormData?.endDate;
});

const isCurrentRole = computed(() => {
  return resumeStore.workExperienceFormData?.isCurrentRole;
});

const businessDescription = computed(() => {
  return resumeStore.workExperienceFormData?.businessDescription;
});

const impact = computed(() => {
  return resumeStore.workExperienceFormData?.impact;
});

const isFormSaving = ref(false);
const isWorkExperienceDeleting = ref(false);

const setValue = <K extends keyof WorkExperience>(
  property: K,
  value: WorkExperience[K] | null | undefined
) => {
  if (value == null || !property) return;
  resumeStore.setWorkExperienceFormValue(property, value);
};

const saveForm = async () => {
  skipOnRouteBeforeLeave.value = true;
  errorStore.setSaveFormError(false);
  try {
    isFormSaving.value = true;
    if (workExperienceId.value) {
      await resumeStore.updateWorkExperience(workExperienceId.value);
    } else {
      await resumeStore.createWorkExperience();
      // TODO: When do we declare that work experiences are completed?
      await resumeStore.updateResumeProperty(
        "isWorkExperiencesCompleted",
        true
      );
    }
    router.push({
      name: "ResumeWorkExperiences",
    });
  } catch (error) {
    console.error("An error occured saving the form: ", error);
    errorStore.setSaveFormError(true);
  } finally {
    isFormSaving.value = false;
  }
};

const triggerDelete = () => {
  triggerDeleteModal.value = true;
};

const deleteWorkExperience = async () => {
  try {
    skipOnRouteBeforeLeave.value = true;
    isWorkExperienceDeleting.value = true;
    if (workExperienceId.value) {
      await resumeStore.deleteWorkExperience(workExperienceId.value);
    } else {
      throw new Error("Something went wrong");
    }
    router.push({
      name: "ResumeWorkExperiences",
    });
  } finally {
    isWorkExperienceDeleting.value = false;
    triggerDeleteModal.value = false;
  }
};

const dismissDeleteModal = () => {
  triggerDeleteModal.value = false;
};

const isCurrentRoleChange = (checked: boolean) => {
  setValue("isCurrentRole", checked);
  if (checked) {
    setValue("endDate", "");
    validationMap.value.set("endDate", true);
    const itemEl = document.querySelector(".end-date-input ion-item");
    if (itemEl) {
      itemEl.classList.remove("ion-invalid");
    }
    return;
  }
};

const errorIdPrefix = ref("error-message-disabler");
const saveButtonDescription = ref("");
const updateErrorDescription = () => {
  saveButtonDescription.value = collateErrorsInDescription(
    document,
    errorIdPrefix.value
  );
};

/* Impact input bullet point handling */

/**
 * Add the initial bullet point to the text area if there is no text
 * @param event Focus event
 */
const initialBulletPoint = (
  event: IonTextareaCustomEvent<FocusEvent>
): void => {
  if (event.target.value === "") {
    setValue("impact", "• ");
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData("text");
  const lines = pastedText?.split("\n");

  const sanitisedPastedText = lines
    ?.map((line) => {
      const trimmedLine = line.trim();
      const listIdRegex =
        /^(\d+\.\s|\d+\)\s|[-*•+#→>]\s|^[a-zA-Z]\.\s|^[a-zA-Z]\)\s)/;

      if (listIdRegex.test(trimmedLine)) {
        return "• " + trimmedLine.replace(listIdRegex, "");
      }
      return "• " + trimmedLine;
    })
    .join("\n");

  const target = event.target as HTMLTextAreaElement;
  const existingLines = target.value?.split("\n");

  let result;
  if (existingLines) {
    while (
      existingLines.length > 0 &&
      existingLines[existingLines.length - 1].trim() === "•"
    ) {
      // Remove all empty bullet points
      existingLines.pop();
    }

    const existingText = existingLines.join("\n");
    const selectionStart = target.selectionStart;
    const selectionEnd = target.selectionEnd;

    result = existingText
      ? existingText.slice(0, selectionStart) +
        "\n" +
        sanitisedPastedText +
        existingText.slice(selectionEnd)
      : undefined;
  }

  setValue("impact", result ?? sanitisedPastedText);
};

/**
 * Removes empty bullet point from string
 * @param event Focus event
 */
const removeBulletPoint = (event: IonTextareaCustomEvent<FocusEvent>): void => {
  const text = event.target.value;
  const sanitisedText = text
    ?.split("\n")
    .filter((line) => line.trim() !== "•" && line.trim() !== "")
    .join("\n");
  setValue("impact", sanitisedText);
};

/**
 * Injects a bullet point in a new line when the enter key is pressed
 * @param event Keyboard enter event
 */
const injectBulletPointOnEnter = (event: KeyboardEvent): void => {
  if (event.key === "Enter") {
    event.preventDefault();

    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    const selectionStart = target.selectionStart;
    const selectionEnd = target.selectionEnd;

    // Split the text at the cursor position
    const beforeCursor = value.substring(0, selectionStart);
    const afterCursor = value.substring(selectionEnd);

    setValue("impact", `${beforeCursor}\n• ${afterCursor}`);
  }
};

const returnToPage = () => {
  router.push({
    name: "ResumeWorkExperiences",
  });
};

/* VALIDATION */

const rules = {
  role: {
    required: t("resume.workExperienceForm.roleInput.isRequiredError"),
  },
  startDate: {
    invalidFormat: t(
      "resume.workExperienceForm.startDateInput.invalidFormatError"
    ),
    noFutureDate: t(
      "resume.workExperienceForm.startDateInput.noFutureDateError"
    ),
  },
  endDate: {
    invalidFormat: t(
      "resume.workExperienceForm.endDateInput.invalidFormatError"
    ),
    noFutureDate: t("resume.workExperienceForm.endDateInput.noFutureDateError"),
    noBeforeStartDate: t(
      "resume.workExperienceForm.endDateInput.noBeforeStartDateError"
    ),
  },
  impact: {
    characterLimit: t(
      "resume.workExperienceForm.impactInput.characterLimitError"
    ),
  },
  businessDescription: {
    characterLimit: t(
      "resume.workExperienceForm.businessDescriptionInput.characterLimitError"
    ),
  },
};

const validationMap = ref<Map<keyof WorkExperience, boolean>>(new Map([]));
const initValidationMap = () => {
  validationMap.value
    .set("role", role.value !== undefined && role.value.length > 0)
    .set("startDate", true)
    .set("endDate", true);
};

const errorMessageMap = ref<Map<keyof WorkExperience, string>>(
  new Map([
    ["role", ""],
    ["endDate", ""],
    ["startDate", ""],
    ["impact", ""],
  ])
);

const setValidity = (
  property: keyof WorkExperience,
  valid: boolean,
  error?: string
): void => {
  validationMap.value.set(property, valid);
  errorMessageMap.value.set(property, error ?? "");
  return;
};

const isFormSavingEnabled = computed(() => {
  for (let isValid of validationMap.value.values()) {
    if (!isValid) return false;
  }
  return true;
});

const validateRole = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const name = (event.target.value as string).trim();
  if (!name) {
    itemEl?.classList.add("ion-invalid");
    setValidity("role", false, rules.role.required);
  } else {
    setValidity("role", true);
  }
};

const validateStartDate = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const date = event.target.value as string;
  if (!date) {
    setValidity("startDate", true);
    return;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = parseDate(date);
  if (!validateDateFormat(date)) {
    itemEl?.classList.add("ion-invalid");
    setValidity("startDate", false, rules.startDate.invalidFormat);
  } else if (inputDate && inputDate > today) {
    itemEl?.classList.add("ion-invalid");
    setValidity("startDate", false, rules.startDate.noFutureDate);
  } else {
    setValidity("startDate", true);
  }

  // This is required to dynamically validate the end date when the issue date changes
  const endDateEl = document.querySelector(
    ".end-date-input .input-field"
  ) as unknown as IonInput;
  if (endDateEl) {
    endDateEl.setFocus();
    endDateEl.setBlur();
  }
};

const validateEndDate = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  if (isCurrentRole.value) {
    setValue("endDate", "");
    setValidity("endDate", true);
    return;
  }
  const date = event.target.value as string;
  if (!date) {
    setValidity("endDate", true);
    return;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = parseDate(date);
  const parsedStartDate = parseDate(startDate.value || "");
  if (!validateDateFormat(date)) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.invalidFormat);
  } else if (inputDate && inputDate > today) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.noFutureDate);
  } else if (parsedStartDate && inputDate && inputDate < parsedStartDate) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.noBeforeStartDate);
  } else {
    setValidity("endDate", true);
  }
};

const validateImpact = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const highlights = event.target.value as string;
  if (highlights && highlights.length >= 600) {
    itemEl?.classList.add("ion-invalid");
    errorMessageMap.value.set("impact", rules.impact.characterLimit);
  }
};

const validateBusinessDescription = (
  event: IonInputCustomEvent<FocusEvent>
) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const highlights = event.target.value as string;
  if (highlights && highlights.length >= 600) {
    itemEl?.classList.add("ion-invalid");
    errorMessageMap.value.set(
      "businessDescription",
      rules.businessDescription.characterLimit
    );
  }
};
</script>

<style lang="scss" scoped>
.save-form-btn {
  margin-top: var(--spacing-16);
}

.text-input,
.checkbox-input {
  margin-bottom: var(--spacing-24);
}

.checkbox-input {
  margin-left: var(--spacing-4);
}

.text-input.location-input {
  margin-bottom: var(--spacing-4);
}

.business-description-input {
  margin-bottom: var(--spacing-24);
}

.buttons-container {
  max-width: 312px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  margin-top: var(--spacing-48);
}
</style>
